import { SuspenseRoot } from '@e3dc-react/shell/Elements/Suspense'
import React, { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { Route } from 'react-router-dom'
import { usePushNotificationHandler } from 'src/Libs/notifications/hooks'
import { useReactNativeMessages } from '@e3dc-react/shell/Libs/react-native'
import { getPushNotificationTokenFromReactNative } from 'src/Libs/notifications/notifications.functions'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { getThemedLogoWithProperties } from 'src/Libs/utils'
import useIsWebviewMode from 'src/Hooks/use-is-webview-mode'

const Login = lazy(() => import('src/Routes/Login/Login'))
const Logout = lazy(() => import('src/Routes/Logout'))
const ResetPassword = lazy(() => import('src/Routes/ResetPassword/ResetPassword'))
const CreatePassword = lazy(() => import('src/Routes/User/CreatePassword'))
const NewPassword = lazy(() => import('@e3dc-react/shell/Routes/NewPassword'))
const Register = lazy(() => import('@e3dc-react/shell/Routes/Register/Register'))

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT ?? ''
const appName = process.env.REACT_APP_NAME ?? ''
const mapsToken = process.env.REACT_APP_MAPS_TOKEN ?? ''

/**
 * Router for default user routes
 */
const useDefaultUserRoutes = (): React.ReactNode[] => {
  const [pushToken, setPushToken] = useState<string>()
  const { handlePushNotificationsAfterLogin, handlePushNotificationsBeforeLogout } = usePushNotificationHandler(pushToken)
  const { logout } = useContext(AuthContext)
  useReactNativeMessages({ setPushToken })
  const { logo } = getThemedLogoWithProperties()
  const isWebview = useIsWebviewMode()

  useEffect(() => {
    getPushNotificationTokenFromReactNative(isWebview)
  }, [isWebview])

  return [
    <Route
      key="login"
      path="/login"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <Login callback={handlePushNotificationsAfterLogin} />
        </Suspense>
      }
    />,
    <Route
      key="reset-password"
      path="/reset-password"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <ResetPassword logo={logo} appName={appName} />
        </Suspense>
      }
    />,
    <Route
      key="create-password"
      path="/create-password"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <CreatePassword logo={logo} />
        </Suspense>
      }
    />,
    <Route
      key="new-password"
      path="/new-password"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <NewPassword apiEndpoint={apiEndpoint} logo={logo} />
        </Suspense>
      }
    />,
    <Route
      key="logout"
      path="/logout"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <Logout
            callbackBeforeLogout={() => {
              handlePushNotificationsBeforeLogout()
              logout()
            }}
          />
        </Suspense>
      }
    />,
    <Route
      key="register"
      path="/register"
      element={
        <Suspense fallback={<SuspenseRoot />}>
          <Register logo={logo} apiEndpoint={apiEndpoint} appName={appName} mapsToken={mapsToken} />
        </Suspense>
      }
    />,
  ]
}

export default useDefaultUserRoutes
